// Component renders kept in the browser, for any surface that paints a card again soon after it
// last rendered it: a dashboard's widgets (`Dashboards/dashboardWidgetCache.ts`) and the widget
// management system's previews (the Library and the Layouts editor; widget-preview-speed spec).
//
// Each render is a react-query entry under a key its surface builds from a prefix and the
// identity parts it renders for (organization, component, revision, page context, table
// requests, and whatever else the surface reads). An entry holds the document and when it was
// rendered (the server's `cached_at` for an answer from its render cache), which a frame's
// "Updated N ago" reads; the entry's own update time is when this page received it, which the
// fresh window reads.
//
// An answer under `KEPT_RENDER_FRESH_MS` old is the card's answer: it paints and nothing is
// sent. An older one paints at once, marked refreshing, while the card renders again behind it
// (`ComponentFrame`'s `prefetched`). An entry lives `KEPT_RENDER_KEPT_MS` from its last write
// with nothing reading it (react-query's `gcTime`); reading never drops one by age, so a
// dashboard open past that keeps painting and exporting its widgets. No module state: the store
// is the page's QueryClient.

import type { QueryClient } from "@tanstack/react-query";
import type { PageSelector } from "./builderTypes";
import type { JoinedCard, PrefetchedCard } from "./cardPrefetch";
import type { KeptAnswer, PlacedComponent } from "./pageRenderBatch";
import type { ComponentDocument, TableRequest } from "./types";

/** How long a kept answer stands as the card's own, with no render. */
export const KEPT_RENDER_FRESH_MS = 60_000;
/**
 * How long an older answer is kept to paint while the card renders again: an entry's lifetime
 * from its last write once nothing observes it.
 */
export const KEPT_RENDER_KEPT_MS = 30 * 60_000;

/** A kept render: its document, and when it was rendered (epoch ms). */
export interface KeptRender {
  document: ComponentDocument;
  renderedAt: number;
  /** The kinds the render read, when the server named them (#13380); kept with the render. */
  kindsRead?: string[] | null;
  /**
   * Set while nothing has acted yet on this render being the first to record its revision's kind
   * (#13380); the dashboard clears it once it has.
   */
  kindRecorded?: boolean;
  /**
   * The server answered it from a cache entry past its fresh window (dashboard polish,
   * requirement 15): it paints as an older answer does, while the card renders again.
   */
  stale?: boolean;
  /**
   * Refresh asked for this render again (dashboard-assistant-polish-3 requirement 11): it is
   * `stale` too, and a frame mounting on it shows it dimmed until the fresh answer lands.
   */
  refreshed?: boolean;
  /**
   * A page back in view asked for this render again in the background (round 10 U5): it is not
   * `stale` (the ask goes through the server's cache, and the frame keeps it on screen undimmed),
   * but the page says it is refreshing until the new answer replaces it.
   */
  revalidating?: boolean;
  /**
   * The render asked for behind this answer failed and the card stays on it (#15213): it is
   * the card's answer for now, not one still on its way, and the next render goes behind it.
   */
  renderFailed?: boolean;
}

/**
 * An answer as kept: a cached one dated by the server's render, a live one by now. A server
 * clock ahead of this one never dates a render in the future.
 */
export const keptRenderOf = (
  answer: KeptAnswer,
  now: number = Date.now()
): KeptRender => ({
  document: answer.document,
  renderedAt: Math.min(answer.cachedAt ?? now, now),
  ...(answer.kindsRead !== undefined ? { kindsRead: answer.kindsRead } : {}),
  ...(answer.kindRecorded ? { kindRecorded: true } : {}),
  ...(answer.stale ? { stale: true } : {}),
});

/**
 * A card's render that does not stop at a stale server answer (dashboard polish, requirement 15):
 * an answer from a cache entry past its fresh window is kept and shown at once (`show`), and the
 * card renders again past the cache (`again`), keeping that answer when it lands. Resolves with
 * the answer the card ends on; rejects when the render behind fails, as any render does.
 */
export const renderPastStale = (
  first: Promise<KeptAnswer>,
  again: () => Promise<KeptAnswer>,
  show:
    | ((document: ComponentDocument, renderedAt: number | null) => void)
    | undefined,
  keep: (answer: KeptAnswer) => void
): Promise<KeptAnswer> =>
  first.then((answer) => {
    keep(answer);
    if (!answer.stale) return answer;
    show?.(answer.document, answer.cachedAt);
    return again().then((fresh) => {
      keep(fresh);
      return fresh;
    });
  });

/** A value in one spelling whatever order its object keys came in. */
export const canonical = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonical);
  if (typeof value === "object" && value !== null)
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, canonical((value as Record<string, unknown>)[key])])
    );
  return value;
};

/** Every preview the widget management system keeps (the Library and the Layouts editor). */
export const LIBRARY_PREVIEWS_KEY = ["rms-library-preview"] as const;

/**
 * One preview in the widget management system: the organization, the component at its
 * revision, the page it previews (the record or view, with the viewer's zone and any filters)
 * and its table requests. The Library and the Layouts editor share it, so a preview rendered
 * on one paints on the other.
 */
export const libraryPreviewKey = (
  orgId: string | null | undefined,
  component: PlacedComponent,
  context: PageSelector,
  tables: Readonly<Record<string, TableRequest>>
): readonly unknown[] => [
  ...LIBRARY_PREVIEWS_KEY,
  orgId ?? null,
  component.id,
  component.current_revision_id,
  canonical(context),
  canonical(tables),
];

/**
 * What a mounting card can paint: its fresh answer, an older one to show while it renders
 * again, or one still on its way (a dashboard's tab prefetch) to join.
 */
export const cachedRender = (
  queryClient: QueryClient,
  key: readonly unknown[],
  now: number = Date.now()
): PrefetchedCard | null => {
  const state = queryClient.getQueryState<KeptRender>(key);
  if (!state) return null;
  if (state.fetchStatus === "fetching")
    return {
      status: "pending",
      join: (signal) =>
        new Promise<JoinedCard>((resolve, reject) => {
          const onAbort = () =>
            reject(
              new DOMException("Component render was canceled", "AbortError")
            );
          if (signal.aborted) {
            onAbort();
            return;
          }
          signal.addEventListener("abort", onAbort, { once: true });
          queryClient
            .fetchQuery<KeptRender>({
              queryKey: key,
              staleTime: KEPT_RENDER_FRESH_MS,
            })
            // A stale server answer is handed back as one: the card shows it and goes
            // on to render again, rather than taking it as its own.
            .then(
              (kept) =>
                resolve({
                  document: kept.document,
                  ...(kept.stale ? { staleRenderedAt: kept.renderedAt } : {}),
                }),
              reject
            )
            .finally(() => signal.removeEventListener("abort", onAbort));
        }),
    };
  if (!state.data) return null;
  // An answer the server marked stale is never the card's own, however recently it arrived.
  return now - state.dataUpdatedAt < KEPT_RENDER_FRESH_MS && !state.data.stale
    ? {
        status: "landed",
        document: state.data.document,
        renderedAt: state.data.renderedAt,
      }
    : {
        status: "stale",
        document: state.data.document,
        renderedAt: state.data.renderedAt,
        ...(state.data.refreshed ? { dimmed: true } : {}),
      };
};

/**
 * The document a card shows now, read once its render has answered: its kept answer, or null
 * when its latest render failed (the card shows its failure, not an older answer).
 */
export const shownRender = (
  queryClient: QueryClient,
  key: readonly unknown[]
): ComponentDocument | null => {
  const state = queryClient.getQueryState<KeptRender>(key);
  if (!state) return null;
  if (state.status === "error" && state.errorUpdatedAt >= state.dataUpdatedAt)
    return null;
  const kept = cachedRender(queryClient, key);
  return kept && kept.status !== "pending" ? kept.document : null;
};

/**
 * Keeps a card's failed render as its entry's error. An entry with a fetch of its own in
 * flight (a prefetch of the same card) is left to that fetch, which records its own outcome:
 * writing an idle state under the fetch would end it early.
 */
export const rememberRenderFailure = (
  queryClient: QueryClient,
  key: readonly unknown[],
  error: Error
): void => {
  const query = queryClient.getQueryCache().build<KeptRender>(queryClient, {
    queryKey: key,
    gcTime: KEPT_RENDER_KEPT_MS,
  });
  if (query.state.fetchStatus === "fetching") return;
  query.setState({
    status: "error",
    error,
    errorUpdatedAt: Date.now(),
    fetchStatus: "idle",
  });
};

/**
 * Keeps a card's answer as its entry. `receivedAt` (now, unless a story ages it) dates the
 * entry for the fresh window; the answer's own render time goes with it for the frame's age.
 *
 * Each write starts the entry's lifetime over. `setQueryData` on an existing entry does not
 * reschedule its garbage collection, so an entry nothing observes (a library preview) is
 * rebuilt, which does; one that is observed (a dashboard tab's widgets) or still fetching (a
 * tab prefetch) is left in place, and react-query schedules its collection when that ends.
 */
export const rememberRender = (
  queryClient: QueryClient,
  key: readonly unknown[],
  render: KeptRender,
  receivedAt: number = Date.now()
): void => {
  const cache = queryClient.getQueryCache();
  const existing = cache.find({ queryKey: key, exact: true });
  if (
    existing &&
    existing.getObserversCount() === 0 &&
    existing.state.fetchStatus !== "fetching"
  )
    cache.remove(existing);
  cache.build(queryClient, { queryKey: key, gcTime: KEPT_RENDER_KEPT_MS });
  queryClient.setQueryData<KeptRender>(key, render, { updatedAt: receivedAt });
};
